import { createContext, useContext, useMemo, useRef, type ReactNode } from "react";
import { Effect, Schema } from "effect";
import { Atom, type AsyncResult } from "effect/unstable/reactivity";
import { useAtomMount, useAtomSet, useAtomValue } from "@effect/atom-react";
import type { DesktopTheme } from "../../opencode/theme/types.ts";
import type { FrontendRoot } from "../root-owner.ts";
import { availableThemes, getTheme, type ThemeSnapshot } from "./theme-model.ts";
import { FrontendThemes, type ThemeController } from "./theme-service.ts";

export interface ThemeRoot { readonly atoms: FrontendRoot<FrontendThemes>["atoms"] }
const ThemeRootContext = createContext<ThemeRoot | undefined>(undefined);
function useThemeController(initial?: string) {
  const root = useContext(ThemeRootContext);
  const descriptor = useMemo<Atom.Atom<AsyncResult.AsyncResult<{ readonly root: ThemeRoot; readonly controller: ThemeController } | undefined>>>(() => root
    ? root.atoms.atom(Effect.gen(function* () { return { root, controller: yield* (yield* FrontendThemes).open }; })).pipe(Atom.setIdleTTL(0))
    : Atom.make(Effect.succeed(undefined)), [root]);
  const opened = useAtomValue(descriptor), controller = opened._tag === "Success" && opened.value?.root === root ? opened.value?.controller : undefined;
  const initialize = useMemo(() => root && controller && initial !== undefined
    ? root.atoms.atom(controller.initialize(initial)).pipe(Atom.setIdleTTL(0)) : Atom.make(Effect.void), [root, controller]);
  useAtomMount(initialize);
  const empty = useMemo(() => Atom.make(Effect.succeed<ThemeSnapshot | undefined>(undefined)), []);
  const observed = useAtomValue(controller?.snapshot ?? empty), snapshot = observed._tag === "Success" ? observed.value : undefined;
  const action = useMemo(() => root && controller ? root.atoms.fn((id: string) => controller.select(id)) : Atom.fn((_: string) => Effect.void), [root, controller]);
  return { snapshot, select: useAtomSet(action) };
}
function ThemeRootFrame({ children }: { readonly children: ReactNode }) {
  const { snapshot } = useThemeController();
  return <div data-workerstation-theme={snapshot?.scopeId} style={{ display: "contents" }}>{children}</div>;
}
export function ThemeRootProvider({ value, children }: { readonly value: ThemeRoot; readonly children: ReactNode }) {
  const parent = useContext(ThemeRootContext);
  if (parent?.atoms === value.atoms) return <>{children}</>;
  return <ThemeRootContext.Provider value={value}><ThemeRootFrame>{children}</ThemeRootFrame></ThemeRootContext.Provider>;
}
export interface OpencodeThemeContextValue {
  readonly themeId: string;
  readonly theme: DesktopTheme;
  readonly setThemeId: (id: string) => void;
  readonly availableThemes: Array<{ id: string; name: string }>;
}
const OpencodeThemeContext = createContext<OpencodeThemeContextValue | null>(null);
export interface OpencodeThemeProviderProps { readonly children: ReactNode; readonly defaultThemeId?: string }
export function OpencodeThemeProvider({ children, defaultThemeId = "oc-2" }: OpencodeThemeProviderProps) {
  const initial = useRef(Schema.is(Schema.String)(defaultThemeId) ? defaultThemeId : "oc-2");
  const { snapshot, select } = useThemeController(initial.current);
  const themeId = snapshot && (snapshot.initialized || snapshot.error !== null) ? snapshot.themeId : initial.current;
  return <OpencodeThemeContext.Provider value={{ themeId, theme: getTheme(themeId), setThemeId: id => select(id), availableThemes: availableThemes() }}>{children}</OpencodeThemeContext.Provider>;
}
export function useOpencodeTheme(): OpencodeThemeContextValue {
  const value = useContext(OpencodeThemeContext);
  return value ?? { themeId: "oc-2", theme: getTheme("oc-2"), setThemeId: () => {}, availableThemes: availableThemes() };
}
